import { useState } from 'react';
import type { FormEvent } from 'react';
import { site } from '@/mocks/gossip';

const FORM_URL = 'https://readdy.ai/api/form/dagka21tto0ceqqnhtt0';

export default function Subscribe() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐检测：命中则静默视为成功，不提交
    const honey = String(data.get('website_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('loading');
    setFormError('');
    const payload = new URLSearchParams();
    for (const [k, v] of data.entries()) {
      if (k === 'website_alt') continue;
      const val = typeof v === 'string' ? v.trim() : '';
      if (val) payload.append(k, val);
    }

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const text = await res.text();
      let code = '';
      let serverMsg = '';
      try {
        const parsed = JSON.parse(text);
        code = parsed?.code ?? '';
        serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      } catch {
        code = res.ok ? 'OK' : '';
      }
      const isSpam = /spam|form data is spam/i.test(serverMsg);
      if (res.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(isSpam || !serverMsg ? '提交未成功，请稍后再试或联系编辑部。' : serverMsg);
      }
    } catch {
      setStatus('error');
      setFormError('网络似乎开小差了，请稍后重试。');
    }
  };

  return (
    <section id="about" className="border-t border-background-200 bg-background-100/60 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <div>
            <p className="mb-3 inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-500" />
              ABOUT US
            </p>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">关于{site.brand}</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-800">
              {site.brand}是一份专注明星八卦的图文资讯周刊。我们把散落在活动通稿、剧组物料、综艺与颁奖礼上的公开素材，整理成
              <b className="font-bold text-primary-700">合集、长图、深扒与盘点</b>
              ，让关注娱乐八卦的你，不用四处蹲点也能把热门事件一网打尽。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              我们坚持「好读、有料、有分寸」的内容原则：基于公开可查的素材做整理与分析，不做无依据的主观臆测，也不泄露个人隐私。若内容涉及需更正或删除的部分，欢迎随时反馈。
            </p>

            <div className="mt-6 rounded-xl bg-background-50 p-5">
              <div className="flex items-start gap-3">
                <i className="ri-double-quotes-l text-3xl text-accent-400" />
                <p className="text-sm italic leading-7 text-foreground-800">
                  别的站追着热搜跑，{site.brand}把热搜炖成一锅好喝的汤——每一期合集、盘点读完，才惊觉一个晚上的瓜已经全吃明白了。
                </p>
              </div>
              <p className="mt-2 text-right text-xs text-foreground-500">—— 忠实读者的留言</p>
            </div>

            <dl className="mt-5 grid grid-cols-3 gap-3 text-center">
              {[
                ['速递更新', '每日早晚两轮'],
                ['深扒盘点', '每周持续上新'],
                ['长图合集', '即点即存即分享'],
              ].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-background-50 px-2 py-3">
                  <dt className="text-sm font-bold text-foreground-950">{k}</dt>
                  <dd className="mt-1 text-[11px] text-foreground-600">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-xl bg-background-50 p-6 shadow-sm md:p-8">
            <div className="mb-1 flex items-center gap-2 text-primary-600">
              <i className="ri-mail-send-line text-xl" />
              <span className="text-xs font-semibold tracking-widest">NEWSLETTER</span>
            </div>
            <h3 className="mt-1 font-heading text-xl font-black text-foreground-950">订阅八卦速报</h3>
            <p className="mt-2 text-sm leading-6 text-foreground-600">
              每期热门八卦盘点、长图合集上新时，第一时间推送到你的邮箱。输入常用邮箱即可，随时可退订。
            </p>

            <form id="subscribe-form" data-readdy-form="八卦速报订阅" onSubmit={onSubmit} className="mt-5 space-y-3">
              <div>
                <label htmlFor="nick" className="mb-1 block text-xs font-medium text-foreground-700">怎么称呼你（选填）</label>
                <input
                  id="nick"
                  name="nickname"
                  type="text"
                  placeholder="例如：爱吃的瓜友"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div>
                <label htmlFor="sub-email" className="mb-1 block text-xs font-medium text-foreground-700">常用邮箱</label>
                <input
                  id="sub-email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div className="gossip-fields" aria-hidden="true">
                <label htmlFor="website_alt">Website</label>
                <input id="website_alt" name="website_alt" type="text" tabIndex={-1} autoComplete="off" readOnly />
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'loading' ? '正在订阅…' : '免费订阅八卦速报'}
              </button>
            </form>

            {status === 'success' && (
              <p className="mt-3 rounded-lg bg-accent-100 px-4 py-2.5 text-sm text-accent-900" role="status">
                <i className="ri-checkbox-circle-line mr-1" />
                订阅成功！新一期八卦速报上线时就会送达你的邮箱，欢迎常回瓜田。
              </p>
            )}
            {status === 'error' && (
              <p className="mt-3 rounded-lg bg-primary-100 px-4 py-2.5 text-sm text-primary-900" role="alert">
                <i className="ri-error-warning-line mr-1" />
                {formError}
              </p>
            )}

            <p className="mt-4 flex items-center justify-between text-[11px] text-foreground-500">
              <span className="inline-flex items-center gap-1">
                <i className="ri-shield-check-line text-accent-600" />
                不投递骚扰邮件，可随时退订
              </span>
              <a href={`mailto:${site.email}`} className="inline-flex items-center gap-1 hover:text-primary-600">
                <i className="ri-mail-line" />
                {site.email}
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}